import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { formatDateBr } from "@/lib/gente-gestao/format";
import { copyText } from "@/lib/gente-gestao/format";
import { fetchColaboradoresOpcoes } from "@/services/genteGestao";
import {
  TIPO_SAIDA_OPCOES,
  criarDesligamento,
  extractDesligamentoError,
  fetchDesligamento,
  fetchDesligamentos,
  type DesligamentoDetalhe,
  type DesligamentoListaItem,
  type DesligamentoResumo,
} from "@/services/desligamento";

const RESUMO_VAZIO: DesligamentoResumo = { pendentes: 0, respondidas: 0, vencidas: 0 };

export function GenteGestaoDesligamentoPage() {
  const [items, setItems] = useState<DesligamentoListaItem[]>([]);
  const [resumo, setResumo] = useState<DesligamentoResumo>(RESUMO_VAZIO);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [criar, setCriar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [colaboradores, setColaboradores] = useState<Array<{ id: string; nome: string; setor: string | null }>>([]);
  const [form, setForm] = useState({ colaboradorId: "", tipoSaida: "PEDIDO", dataDesligamento: "", entrevistador: "", prazo: "" });
  const [detalhe, setDetalhe] = useState<DesligamentoDetalhe | null>(null);
  const [copiado, setCopiado] = useState(false);

  async function load() {
    setLoading(true);
    setErro(null);
    try {
      const res = await fetchDesligamentos();
      setItems(res.entrevistas);
      setResumo(res.resumo);
    } catch (e) {
      setErro(extractDesligamentoError(e, "Não foi possível carregar as entrevistas."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function abrirCriar() {
    setErro(null);
    setCriar(true);
    if (colaboradores.length === 0) {
      try {
        setColaboradores(await fetchColaboradoresOpcoes());
      } catch (e) {
        setErro(extractDesligamentoError(e, "Não foi possível carregar os colaboradores."));
      }
    }
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const criada = await criarDesligamento(form);
      setCriar(false);
      setDetalhe(criada);
      setForm({ colaboradorId: "", tipoSaida: "PEDIDO", dataDesligamento: "", entrevistador: "", prazo: "" });
      await load();
    } catch (e) {
      setErro(extractDesligamentoError(e, "Não foi possível criar a entrevista."));
    } finally {
      setSalvando(false);
    }
  }

  async function abrir(id: string) {
    setErro(null);
    setCopiado(false);
    try {
      setDetalhe(await fetchDesligamento(id));
    } catch (e) {
      setErro(extractDesligamentoError(e, "Não foi possível abrir a entrevista."));
    }
  }

  async function copiar(texto: string) {
    const ok = await copyText(texto);
    setCopiado(ok);
  }

  const respostas = detalhe?.respostas;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-800">Entrevista de desligamento</h2>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-600">
            Envie um link individual para o colaborador responder online. A participação é voluntária e as respostas ficam
            guardadas aqui, separadas do Canal de Escuta.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => void abrirCriar()}>
          Nova entrevista
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card title="Aguardando" value={String(resumo.pendentes)} />
        <Card title="Respondidas" value={String(resumo.respondidas)} />
        <Card title="Prazo encerrado" value={String(resumo.vencidas)} />
      </div>

      {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-card">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader label="Carregando entrevistas..." />
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-500">Nenhuma entrevista criada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Colaborador</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Prazo</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => void abrir(item.id)}>
                  <td className="px-4 py-3">
                    <span className="font-medium">{item.nome}</span>
                    <span className="block text-xs text-slate-500">{[item.cargo, item.setor].filter(Boolean).join(" · ") || "—"}</span>
                  </td>
                  <td className="px-4 py-3">{item.tipoLabel}</td>
                  <td className="px-4 py-3">{formatDateBr(item.prazo)}</td>
                  <td className="px-4 py-3">{item.statusLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={criar} onClose={() => setCriar(false)} title="Nova entrevista" closeOnEscape closeOnBackdropClick
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCriar(false)}>Cancelar</Button>
            <Button loading={salvando} onClick={() => void salvar()}>Gerar link</Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Select
            label="Colaborador"
            value={form.colaboradorId}
            options={colaboradores.map((c) => ({ value: c.id, label: c.setor ? `${c.nome} · ${c.setor}` : c.nome }))}
            onChange={(e) => setForm({ ...form, colaboradorId: e.target.value })}
          />
          <Select label="Tipo de desligamento" value={form.tipoSaida} options={TIPO_SAIDA_OPCOES} onChange={(e) => setForm({ ...form, tipoSaida: e.target.value })} />
          <Input label="Data do desligamento" type="date" value={form.dataDesligamento} onChange={(e) => setForm({ ...form, dataDesligamento: e.target.value })} />
          <Input label="Entrevistador" value={form.entrevistador} onChange={(e) => setForm({ ...form, entrevistador: e.target.value })} hint="Opcional. Quem acompanha o processo no RH." />
          <Input label="Prazo para responder" type="date" value={form.prazo} onChange={(e) => setForm({ ...form, prazo: e.target.value })} hint="Opcional. Depois desta data o link encerra." />
        </div>
      </Modal>

      <Modal open={detalhe != null} onClose={() => setDetalhe(null)} title={detalhe?.nome ?? "Entrevista"} size="lg" closeOnEscape closeOnBackdropClick>
        {detalhe && (
          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
            <p className="text-sm text-slate-500">
              {detalhe.tipoLabel}
              {detalhe.dataDesligamento ? ` · saída em ${formatDateBr(detalhe.dataDesligamento)}` : ""}
              {detalhe.gestorNome ? ` · liderança ${detalhe.gestorNome}` : ""}
            </p>
            <p className="text-sm font-medium text-slate-700">{detalhe.statusLabel}</p>
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Link individual</p>
              <p className="mt-1 break-all text-sm text-slate-700">{detalhe.link}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => void copiar(detalhe.link)}>{copiado ? "Copiado" : "Copiar link"}</Button>
                <Button size="sm" variant="ghost" onClick={() => void copiar(detalhe.mensagem)}>Copiar mensagem</Button>
              </div>
            </div>
            {respostas ? (
              <div className="flex flex-col gap-3 text-sm text-slate-700">
                <p><strong>Motivo:</strong> {respostas.motivoLabel}</p>
                <p><strong>O que influenciou:</strong> {respostas.influencia}</p>
                <p><strong>Liderança:</strong> {respostas.liderancaLabel}{respostas.liderancaPorque ? ` — ${respostas.liderancaPorque}` : ""}</p>
                <p><strong>Respeito e valorização:</strong> {respostas.respeitoLabel}</p>
                <p><strong>Condições:</strong> {respostas.condicoesLabel}{respostas.condicoesFaltava ? ` — ${respostas.condicoesFaltava}` : ""}</p>
                <p><strong>Clareza:</strong> {respostas.clarezaLabel}</p>
                <p><strong>Crescimento:</strong> {respostas.crescimentoLabel}{respostas.crescimentoComente ? ` — ${respostas.crescimentoComente}` : ""}</p>
                <p><strong>Problema não resolvido:</strong> {respostas.problemaLabel}{respostas.problemaDescricao ? ` — ${respostas.problemaDescricao}` : ""}</p>
                <p><strong>O que faria permanecer:</strong> {respostas.permanecer}</p>
                <p><strong>Manter:</strong> {respostas.manter}</p>
                <p><strong>Melhorar:</strong> {respostas.melhorar}</p>
                {respostas.observacoes && <p><strong>Observações:</strong> {respostas.observacoes}</p>}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Ainda não há resposta. Envie o link somente para esta pessoa.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
